import { API_BASE } from '../config';
import { DragDropContext, Draggable, Droppable } from '@hello-pangea/dnd';
import { useEffect, useRef, useState } from 'react';
import './pages.css';
import StarRating from './StarRating';
import QuestionEditor from './QuestionEditor';


const AdminSurveyManagePageRight = ({
  questions,
  setQuestions,
  newQuestion,
  setNewQuestion,
  addQuestion,
  updateQuestion,
  deleteQuestion,
  questionTypes,
  addOptionToQuestion,
  selectSurvey,
  selectedSurveyId,
}) => {
  // ------- 상태 -------
  const [editingOptionIndex, setEditingOptionIndex] = useState(null);
  const [editingOptionValue, setEditingOptionValue] = useState('');
  const [openOptionEditorIds, setOpenOptionEditorIds] = useState([]);
  const [newOptionValue, setNewOptionValue] = useState(''); // 새 문항 옵션
  const [newOptionValueForEdit, setNewOptionValueForEdit] = useState(''); // 기존 문항 옵션 추가
  const [isCopying, setIsCopying] = useState(false);
  const [lockAfterSubmit, setLockAfterSubmit] = useState(0);
  const [reorderEnabled, setReorderEnabled] = useState(false);
  const [orderDrafts, setOrderDrafts] = useState({});
  const [isSavingOrder, setIsSavingOrder] = useState(false);
  const [orderError, setOrderError] = useState('');
  const savingOrderRef = useRef(false);
  const orderContextRef = useRef(0);

  useEffect(() => {
    orderContextRef.current += 1;
    setOrderDrafts({});
    setOrderError('');
  }, [selectedSurveyId]);

  useEffect(() => { setOrderDrafts({}); }, [questions, reorderEnabled]);

  // 설문 복사용 상태
  const [programs, setPrograms] = useState([]);
  const [sites, setSites] = useState([]);
  const [copyProgramId, setCopyProgramId] = useState('');
  const [copySiteId, setCopySiteId] = useState('');
  const [copyTitle, setCopyTitle] = useState('');
  const [copyMode, setCopyMode] = useState('new');
  const [copySurveyId, setCopySurveyId] = useState('');
  const [copySurveys, setCopySurveys] = useState([]);
  const [isLoadingCopySurveys, setIsLoadingCopySurveys] = useState(false);
  const [copySurveysError, setCopySurveysError] = useState('');
  const [copyOpen, setCopyOpen] = useState(false);
  const [copyQuestionIds, setCopyQuestionIds] = useState([]);
  const selectedCopyIds = questions
    .filter(q => copyQuestionIds === null || copyQuestionIds.includes(Number(q.question_id)))
    .map(q => Number(q.question_id));

  useEffect(() => {
    setCopyOpen(false);
    setCopyQuestionIds([]);
    setCopySurveyId('');
  }, [selectedSurveyId]);

  const handleCopy = async () => {
    if (isCopying || savingOrderRef.current || selectedCopyIds.length === 0) return;
    const appendToExisting = copyMode === 'existing';
    if (!selectedSurveyId || !copySiteId || (appendToExisting
      ? !copySurveyId || isLoadingCopySurveys || !!copySurveysError || !copySurveys.some(s => Number(s.survey_id) === Number(copySurveyId))
      : !copyTitle.trim())) {
      alert(appendToExisting ? '추가 대상 사이트와 기존 설문을 선택해 주세요.' : '원본 설문, 복사 대상 사이트와 새 설문 제목을 확인해 주세요.');
      return;
    }
    setIsCopying(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/api/surveys/copy`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          source_survey_id: selectedSurveyId,
          target_site_id: Number(copySiteId),
          ...(appendToExisting ? { target_survey_id: Number(copySurveyId) } : { title: copyTitle.trim() }),
          question_ids: selectedCopyIds,
        }),
      });
      if (!res.ok) throw new Error('copy_failed');
      const result = await res.json();
      alert(appendToExisting ? '기존 설문에 선택 문항을 추가했습니다.' : '복사 완료!');
      const destinationId = result.target_survey_id || result.new_survey_id;
      if (destinationId) selectSurvey(destinationId);
      setCopyProgramId('');
      setCopySiteId('');
      setCopyTitle('');
      setCopySurveyId('');
      setCopyQuestionIds([]);
      setCopyOpen(false);
    } catch {
      alert('복사에 실패했습니다. 선택 문항과 연결 상태를 확인한 뒤 다시 시도해 주세요.');
    } finally {
      setIsCopying(false);
    }
  };

  // ------- 스크롤 제어(pin) -------
  const scrollContainerRef = useRef(null);
  const itemRefs = useRef(new Map());
  const setItemRef = (id) => (el) => {
    if (el) itemRefs.current.set(id, el);
    else itemRefs.current.delete(id);
  };
  const setScrollTop = (top) => {
    const el = scrollContainerRef.current;
    if (el) el.scrollTo({ top, behavior: 'auto' });
  };
  const pinCardDuring = async (el, stateUpdater) => {
    const sc = scrollContainerRef.current;
    if (!el || !sc) {
      stateUpdater();
      return;
    }
    const before = el.getBoundingClientRect().top;
    stateUpdater();
    await new Promise((r) => requestAnimationFrame(r));
    const after = el.getBoundingClientRect().top;
    const delta = after - before;
    if (delta) setScrollTop(sc.scrollTop + delta);
  };

  // ------- 인라인 편집 -------
  const [editingId, setEditingId] = useState(null);
  const [savedQuestionId, setSavedQuestionId] = useState(null);
  useEffect(() => { setEditingId(null); setSavedQuestionId(null); }, [selectedSurveyId]);

  // ------- 유틸 -------
  const getDisplayName = (typeId) => {
    const found = questionTypes.find((t) => t.type_id === typeId);
    return found ? found.display_name : String(typeId);
  };
  const isChoiceSingle = (q) => Number(q.question_type_id) === 2;
  const isChoiceMulti = (q) => Number(q.question_type_id) === 4;
  const isStarType = (q) => Number(q.question_type_id) === 3;
  const isImageWithComment = (q) => Number(q.question_type_id) === 6;

  // ------- 메타 로드(잠금) -------
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!selectedSurveyId) {
      setLockAfterSubmit(0);
      return;
    }
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/surveys/${selectedSurveyId}/meta`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const meta = await res.json();
          setLockAfterSubmit(meta.lock_after_submit ?? 0);
        }
      } catch (e) {
        console.error('load survey meta error', e);
      }
    })();
  }, [selectedSurveyId]);

  // 새 문항: 객관/다중 아닐 때 옵션 초기화
  useEffect(() => {
    if (![2, 4].includes(Number(newQuestion.question_type_id))) {
      setNewQuestion((prev) => ({ ...prev, options: [], includeEtc: false }));
    }
  }, [newQuestion.question_type_id, setNewQuestion]);

  // 제출 후 수정 가능 토글
  const handleToggleLock = async (checked) => {
    const next = checked ? 0 : 1;
    setLockAfterSubmit(next);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/api/surveys/${selectedSurveyId}/lock`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ lock_after_submit: next }),
      });
      if (!res.ok) throw new Error('서버 업데이트 실패');
    } catch (e) {
      console.error('toggle lock error', e);
    }
  };

  // ------- 복사용 보조 로드 -------
  useEffect(() => {
    const token = localStorage.getItem('token');
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/programs`, { headers: { Authorization: `Bearer ${token}` } });
        if (res.ok) setPrograms(await res.json());
      } catch {}
    })();
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    (async () => {
      try {
        const url = copyProgramId ? `${API_BASE}/api/sites?program_id=${copyProgramId}` : `${API_BASE}/api/sites`;
        const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
        if (res.ok) setSites(await res.json());
      } catch {}
    })();
    setCopySiteId('');
  }, [copyProgramId]);

  useEffect(() => {
    let cancelled = false;
    setCopySurveyId('');
    setCopySurveys([]);
    setCopySurveysError('');
    setIsLoadingCopySurveys(false);
    if (!copyOpen || copyMode !== 'existing' || !copySiteId) return;
    setIsLoadingCopySurveys(true);
    const token = localStorage.getItem('token');
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/surveys?site_id=${copySiteId}`, { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) throw new Error('surveys_fetch_failed');
        const surveys = await res.json();
        if (!cancelled) setCopySurveys(surveys.filter(s => Number(s.site_id) === Number(copySiteId)));
      } catch {
        if (!cancelled) setCopySurveysError('설문 목록을 불러오지 못했습니다. 사이트를 다시 선택해 주세요.');
      } finally {
        if (!cancelled) setIsLoadingCopySurveys(false);
      }
    })();
    return () => { cancelled = true; };
  }, [copyOpen, copyMode, copySiteId]);

  // ------- DnD -------
  const lastScrollTopRef = useRef(0);
  const onBeforeDragStart = () => {
    if (!reorderEnabled) return;
    const sc = scrollContainerRef.current;
    lastScrollTopRef.current = sc ? sc.scrollTop : 0;
  };
  const moveQuestion = async (fromIndex, toIndex) => {
    if (!reorderEnabled || savingOrderRef.current || isCopying || editingId !== null || fromIndex === toIndex) return;
    const context = orderContextRef.current;
    savingOrderRef.current = true;
    setIsSavingOrder(true);
    setOrderError('');
    setOrderDrafts({});
    const reordered = Array.from(questions);
    const [movedItem] = reordered.splice(fromIndex, 1);
    reordered.splice(toIndex, 0, movedItem);
    try {
      const token = localStorage.getItem('token');
      for (let i = 0; i < reordered.length; i++) {
        const res = await fetch(`${API_BASE}/api/questions/${reordered[i].question_id}/order`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ order_index: i + 1 }),
        });
        if (!res.ok) throw new Error('order_save_failed');
      }
      if (context === orderContextRef.current) {
        setQuestions(reordered.map((q, i) => ({ ...q, order_index: i + 1 })));
      }
    } catch {
      if (context === orderContextRef.current) {
        setOrderError('순서 저장에 실패했습니다. 일부 변경이 저장되었을 수 있으니 설문을 다시 확인해 주세요.');
        // 개별 저장 API이므로 부분 저장된 경우 서버의 실제 순서를 다시 불러온다.
        try {
          const res = await fetch(`${API_BASE}/api/surveys/${selectedSurveyId}/questions`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          });
          if (!res.ok) throw new Error('order_reload_failed');
          const currentQuestions = await res.json();
          if (context === orderContextRef.current) setQuestions(currentQuestions);
        } catch {
          // 재조회도 실패하면 오류 안내와 현재 목록을 유지한다.
        }
      }
    } finally {
      savingOrderRef.current = false;
      setIsSavingOrder(false);
    }
  };

  const clearOrderDraft = (questionId) => setOrderDrafts(prev => {
    const next = { ...prev };
    delete next[questionId];
    return next;
  });

  const commitOrder = (questionId, index) => {
    if (!reorderEnabled || savingOrderRef.current || editingId !== null) return;
    const draft = orderDrafts[questionId];
    if (draft === undefined) return;
    clearOrderDraft(questionId);
    const position = Number(draft);
    if (!/^\d+$/.test(draft) || !Number.isInteger(position) || position < 1 || position > questions.length) {
      setOrderError(`순서는 1~${questions.length} 사이의 정수로 입력해 주세요.`);
      return;
    }
    setOrderError('');
    moveQuestion(index, position - 1);
  };

  const handleDragEnd = async (result) => {
    if (!result.destination) return;
    await moveQuestion(result.source.index, result.destination.index);
    if (scrollContainerRef.current?.scrollTo) setScrollTop(lastScrollTopRef.current);
  };

  // ========= 옵션 대량 편집용 Draft 상태(문항별) =========
  // optionDrafts[qid] = { items: string[], includeEtc: boolean, dirty: boolean }
  const [optionDrafts, setOptionDrafts] = useState({});
  // 저장 완료 토스트 표시용
  const [optionSavedMap, setOptionSavedMap] = useState({}); // { [qid]: boolean }

  // 옵션 편집 열기/닫기 + 초깃값 주입
  const toggleOptionEditor = (questionId) => {
    const el = itemRefs.current.get(questionId);
    pinCardDuring(el, () => {
      setOpenOptionEditorIds((prev) => {
        const isOpen = prev.includes(questionId);
        const next = isOpen ? prev.filter((id) => id !== questionId) : [...prev, questionId];
        // 열 때 현재 옵션으로 draft 초기화
        if (!isOpen) {
          const q = questions.find((qq) => qq.question_id === questionId);
          const opts = q?.options || [];
          const hasEtc = opts.includes('기타');
          const pureOpts = hasEtc ? opts.filter((o) => o !== '기타') : [...opts];
          setOptionDrafts((d) => ({
            ...d,
            [questionId]: { items: pureOpts, includeEtc: hasEtc, dirty: false },
          }));
        } else {
          // 닫을 때 임시 편집 인덱스/값 초기화
          setEditingOptionIndex(null);
          setEditingOptionValue('');
          setNewOptionValueForEdit('');
        }
        return next;
      });
    });
  };

  // 한 문항의 옵션 드래프트 편집 핸들러
  const patchDraft = (qid, patch) =>
    setOptionDrafts((d) => ({ ...d, [qid]: { ...(d[qid] || { items: [], includeEtc: false, dirty: false }), ...patch, dirty: true } }));

  // 옵션 대량 저장
  const saveOptionsFor = (q) => {
    const draft = optionDrafts[q.question_id];
    if (!draft) return;
    let finalOpts = [...draft.items].map((s) => (s || '').trim()).filter((s) => !!s);
    if (draft.includeEtc && !finalOpts.includes('기타')) finalOpts.push('기타');
    if (!draft.includeEtc) finalOpts = finalOpts.filter((s) => s !== '기타');

    updateQuestion(q.question_id, { ...q, options: finalOpts });

    // 편집창 닫기 + 성공 토스트
    setOpenOptionEditorIds((prev) => prev.filter((id) => id !== q.question_id));
    setOptionSavedMap((m) => ({ ...m, [q.question_id]: true }));
    // 1.5초 뒤 메시지 제거
    setTimeout(() => {
      setOptionSavedMap((m) => ({ ...m, [q.question_id]: false }));
    }, 1500);

    // 드래프트 정리
    setOptionDrafts((d) => ({ ...d, [q.question_id]: { ...(d[q.question_id] || {}), dirty: false } }));
    // 행별 임시 상태 정리
    setEditingOptionIndex(null);
    setEditingOptionValue('');
    setNewOptionValueForEdit('');
  };

  const toggleRequired = (question) => updateQuestion(question.question_id, { ...question, required: !question.required });

  // 유형 전환(객관식<->다중선택)
  const toggleChoiceType = (q) => {
    const nextType = isChoiceSingle(q) ? 4 : 2;
    updateQuestion(q.question_id, { ...q, question_type_id: nextType });
  };

  const startEdit = async (q) => {
    if (editingId && editingId !== q.question_id && !window.confirm('편집 중인 내용을 닫고 다른 문항을 수정할까요?')) return;
    const el = itemRefs.current.get(q.question_id);
    await pinCardDuring(el, () => {
      setEditingId(q.question_id);
      setSavedQuestionId(null);
    });
    const editor = el?.querySelector('.qe');
    const panel = el?.closest('.admin-right-section');
    if (editor && panel) {
      panel.scrollTop += editor.getBoundingClientRect().top - panel.getBoundingClientRect().top - 12;
    }
  };
  const cancelEdit = () => {
    const el = itemRefs.current.get(editingId);
    pinCardDuring(el, () => {
      setEditingId(null);
    });
    requestAnimationFrame(() => el?.querySelector('[data-edit-question]')?.focus({ preventScroll: true }));
  };
  const saveEdit = async (q, draft) => {
    const result = await updateQuestion(q.question_id, { ...q, ...draft });
    if (result === false) return false;
    cancelEdit();
    setSavedQuestionId(q.question_id);
    return true;
  };

  // ------- 미리보기 -------
  const ChoicePreview = ({ q }) => {
    const opts = q.options || [];
    if (opts.length === 0) return <div className="sub-muted">옵션이 없습니다.</div>;
    return (
      <div className="choice-preview" style={{ marginTop: 8, display: 'grid', gap: 6 }}>
        {opts.map((opt, i) => (
          <label key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {isChoiceSingle(q) ? <input type="radio" disabled name={`q${q.question_id}-preview`} /> : <input type="checkbox" disabled />}
            <span>{opt}</span>
          </label>
        ))}
        {opts.includes('기타') && (
          <div style={{ marginTop: 4, display: 'flex', gap: 8, alignItems: 'center' }}>
            {isChoiceSingle(q) ? <input type="radio" disabled name={`q${q.question_id}-preview`} /> : <input type="checkbox" disabled />}
            <input className="input-beauty" style={{ flex: 1 }} disabled placeholder="기타 내용을 입력하세요" />
          </div>
        )}
      </div>
    );
  };

  const StarPreview = ({ q }) => {
    const max = Number(q.max_score ?? 5);
    const step = Number(q.step ?? 1);
    return (
      <div style={{ marginTop: 8 }}>
        <StarRating value={Number(q.preview_value ?? 0)} onChange={() => {}} max={max} step={step} readOnly />
        <div className="sub-muted" style={{ marginTop: 4 }}>
          (미리보기 · 최대 {max}점, 간격 {step})
        </div>
      </div>
    );
  };

  const ImageCommentPreview = () => (
    <div style={{ marginTop: 8 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <input type="file" accept="image/*" disabled />
        <span className="sub-muted">(미리보기 · 이미지 업로드 + 멘트)</span>
      </div>
      <textarea className="input-beauty" disabled placeholder="멘트를 입력하세요" rows={2} style={{ width: '100%', marginTop: 6 }} />
    </div>
  );

  // ------- 렌더 -------
  return (
    <div className="admin-right-section">
      <h3>📁문항 관리</h3>

      <div className="card" style={{ marginBottom: 12, padding: 12, display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
        {selectedSurveyId && (
          <>
            <strong style={{ marginRight: 8 }}>설문 설정</strong>
            <label className="question-explain" style={{ cursor: 'pointer' }}>
              <input type="checkbox" checked={lockAfterSubmit === 0} onChange={(e) => handleToggleLock(e.target.checked)} /> 제출 후 수정 가능
            </label>
          </>
        )}

        {/* 설문 복사 토글 */}
        <label className="question-explain" style={{ cursor: 'pointer' }}>
          <input type="checkbox" checked={copyOpen} disabled={isCopying || isSavingOrder || !selectedSurveyId} onChange={(e) => {
            setCopyOpen(e.target.checked);
            setCopyQuestionIds(e.target.checked ? null : []);
          }} /> 설문 복사
        </label>

        {/* 설문 복사 UI */}
        {copyOpen && (
          <fieldset disabled={isCopying || isSavingOrder} style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', border: 0, padding: 0, margin: 0, width: '100%', minWidth: 0 }}>
            <strong>설문 복사</strong>
            <div className="survey-copy-modes" role="group" aria-label="복사 방식">
              <button type="button" className="survey-copy-mode-button" aria-pressed={copyMode === 'new'}
                onClick={() => setCopyMode('new')}>
                새 설문 복사
              </button>
              <button type="button" className="survey-copy-mode-button" aria-pressed={copyMode === 'existing'}
                onClick={() => setCopyMode('existing')}>
                기존 설문에 복사
              </button>
            </div>
            <select className="input-beauty" value={copyProgramId} onChange={(e) => {
              setCopyProgramId(e.target.value);
              setCopySiteId('');
              setCopySurveyId('');
            }}>
              <option value="">사업 선택</option>
              {programs.map((p) => (
                <option key={p.program_id} value={p.program_id}>
                  {p.name}
                </option>
              ))}
            </select>
            <select className="input-beauty" aria-label="복사 대상 사이트" value={copySiteId} onChange={(e) => {
              setCopySiteId(e.target.value);
              setCopySurveyId('');
            }}>
              <option value="">사이트 선택</option>
              {sites
                .filter((s) => !copyProgramId || s.program_id === Number(copyProgramId))
                .map((s) => (
                  <option key={s.site_id} value={s.site_id}>
                    {s.site_name}
                  </option>
                ))}
            </select>
            {copyMode === 'new' ? (
              <input className="input-beauty" placeholder="새 설문 제목" value={copyTitle} onChange={(e) => setCopyTitle(e.target.value)} />
            ) : (
              <>
                <select className="input-beauty" aria-label="추가 대상 설문" value={copySurveyId}
                  disabled={!copySiteId || isLoadingCopySurveys || !!copySurveysError}
                  onChange={(e) => setCopySurveyId(e.target.value)}>
                  <option value="">{isLoadingCopySurveys ? '설문 목록 불러오는 중…' : '기존 설문 선택'}</option>
                  {copySurveys.map(s => <option key={s.survey_id} value={s.survey_id}>{s.title}</option>)}
                </select>
                <p className="sub-muted" style={{ width: '100%', margin: 0 }}>
                  기존 문항을 유지하고 선택 문항을 원본 순서대로 맨 뒤에 추가합니다.
                </p>
                {copySurveysError && <p role="alert">{copySurveysError}</p>}
                {copySiteId && !isLoadingCopySurveys && !copySurveysError && copySurveys.length === 0 && <p role="status">선택한 사이트에 기존 설문이 없습니다.</p>}
              </>
            )}
            <div style={{ width: '100%', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <strong>복사할 문항 선택</strong>
              <span role="status">전체 {questions.length}개 중 {selectedCopyIds.length}개 선택</span>
              <button type="button" className="detail-add-button" onClick={() => setCopyQuestionIds(null)}>전체 선택</button>
              <button type="button" className="detail-add-button" onClick={() => setCopyQuestionIds([])}>전체 해제</button>
            </div>
            <div style={{ width: '100%', maxHeight: 260, overflowY: 'auto', border: '1px solid #ddd', borderRadius: 8, padding: 12, boxSizing: 'border-box' }}>
              {questions.length === 0 && <p>복사할 문항이 없습니다.</p>}
              {questions.map((q, index) => (
                <label key={q.question_id} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', padding: '8px 0', overflowWrap: 'anywhere' }}>
                  <input type="checkbox" aria-label={`복사 문항 ${index + 1}`} checked={selectedCopyIds.includes(Number(q.question_id))}
                    onChange={(e) => setCopyQuestionIds(e.target.checked
                      ? [...selectedCopyIds, Number(q.question_id)] : selectedCopyIds.filter(id => id !== Number(q.question_id)))} />
                  <span>{index + 1}. [{getDisplayName(q.question_type_id)}] {q.question_text}</span>
                </label>
              ))}
            </div>
            <button
              className="detail-add-button"
              disabled={isCopying || selectedCopyIds.length === 0 || (copyMode === 'existing' && (!copySurveyId || isLoadingCopySurveys || !!copySurveysError))}
              onClick={handleCopy}
            >
              {isCopying ? '복사 중…' : `선택 문항 ${copyMode === 'existing' ? '추가' : '복사'} (${selectedCopyIds.length}개)`}
            </button>
          </fieldset>
        )}

        {/* 순서 편집 토글 */}
        <label className="question-explain" style={{ cursor: 'pointer', marginLeft: 'auto' }}>
          <input type="checkbox" checked={reorderEnabled} disabled={isSavingOrder || isCopying} onChange={(e) => {
            setReorderEnabled(e.target.checked);
            setOrderError('');
          }} /> 순서 편집(번호 입력 또는 드래그)
        </label>
      </div>

      {reorderEnabled && <div className="sub-muted" style={{ marginBottom: 8 }}>안내 문항을 포함한 위치 번호를 입력한 뒤 Enter를 누르거나 다른 곳을 클릭하세요.</div>}
      {isSavingOrder && <div role="status">순서를 저장하고 있습니다…</div>}
      {orderError && <div role="alert" className="question-order-error">{orderError}</div>}

      {/* 목록 + DnD */}
      <DragDropContext onBeforeDragStart={onBeforeDragStart} onDragEnd={handleDragEnd}>
        <Droppable droppableId="question-list" isDropDisabled={!reorderEnabled || isSavingOrder || isCopying || editingId !== null}>
          {(provided) => (
            <ul
              className="question-list"
              ref={(el) => {
                provided.innerRef(el);
                scrollContainerRef.current = el;
              }}
              {...provided.droppableProps}
            >
              {questions.map((q, idx) => {
                const visibleIndex =
                  q.question_type_id !== 10 ? questions.filter((qq) => qq.question_type_id !== 10).findIndex((qq) => qq.question_id === q.question_id) + 1 : null;
                const inEdit = editingId === q.question_id;
                const optionDraft = optionDrafts[q.question_id];

                return (
                  <Draggable key={q.question_id} draggableId={q.question_id.toString()} index={idx} isDragDisabled={!reorderEnabled || isSavingOrder || isCopying || editingId !== null}>
                    {(drag) => (
                      <li
                        ref={(node) => {
                          drag.innerRef(node);
                          setItemRef(q.question_id)(node);
                        }}
                        {...drag.draggableProps}
                        className="question-card"
                      >
                        {/* 저장됨 미니 토스트 */}
                        {optionSavedMap[q.question_id] && (
                          <div className="badge-success" style={{ marginBottom: 6, alignSelf: 'flex-start' }}>
                            옵션이 저장되었습니다.
                          </div>
                        )}

                        {savedQuestionId === q.question_id && <div className="badge-success" role="status">변경사항이 저장되었습니다.</div>}
                        {inEdit ? <QuestionEditor key={q.question_id} question={q} typeName={getDisplayName(q.question_type_id)}
                          onSave={draft => saveEdit(q, draft)} onCancel={cancelEdit} /> : <>

                        {/* 제목 줄 */}
                        <div
                          className="question-title"
                          style={{
                            display: 'flex',
                            gap: 8,
                            flexWrap: 'wrap',
                            alignItems: 'center',
                            minWidth: 0,
                          }}
                        >
                          {reorderEnabled && (
                            <input
                              type="number"
                              className="question-order-input"
                              aria-label={`${q.question_text} 순서`}
                              min={1}
                              max={questions.length}
                              step={1}
                              value={orderDrafts[q.question_id] ?? String(idx + 1)}
                              disabled={isSavingOrder || isCopying || editingId !== null}
                              title={`이동할 위치 (1~${questions.length}) · Enter로 적용`}
                              onChange={e => setOrderDrafts(prev => ({ ...prev, [q.question_id]: e.target.value }))}
                              onBlur={() => commitOrder(q.question_id, idx)}
                              onKeyDown={e => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  commitOrder(q.question_id, idx);
                                } else if (e.key === 'Escape') {
                                  e.preventDefault();
                                  clearOrderDraft(q.question_id);
                                  setOrderError('');
                                }
                              }}
                            />
                          )}
                          {reorderEnabled && (
                            <span {...drag.dragHandleProps} title="드래그하여 순서 변경" style={{ cursor: 'grab', userSelect: 'none' }} aria-label="drag-handle">
                              ⠿
                            </span>
                          )}

                          {(
                            <span style={{ flex: 1, minWidth: 0, wordBreak: 'break-word' }}>
                              {Boolean(q.required) && <span style={{ color: 'red', marginLeft: 4 }}>*</span>}&nbsp;
                              {q.question_type_id !== 10 && `${visibleIndex}. `}
                              [{getDisplayName(q.question_type_id)}]&nbsp;
                              {q.question_text}
                            </span>
                          )}
                        </div>

                        
                        {/* 설명 */}
                        {(
                          q.question_explain && (
                            <div className="question-explain" dangerouslySetInnerHTML={{ __html: (q.question_explain || '').replace(/\n/g, '<br>') }} />
                          )
                        )}

                        {/* 정답 */}
                        {(q.correct_answer ?? '').toString().trim() !== '' && (
                          <div style={{ marginTop: 6 }}>
                            {(
                              <div className="correct-answer">
                                정답: <span style={{ color: 'red' }}>{q.correct_answer}</span>
                              </div>
                            )}
                          </div>
                        )}

                        {/* 미리보기 */}
                        {Number(q.question_type_id) === 11 && (
                          <input type="text" className="input-beauty" disabled placeholder="답변을 입력하세요" aria-label="주관식(단답형) 미리보기" />
                        )}
                        {(isChoiceSingle(q) || isChoiceMulti(q)) && <ChoicePreview q={q} />}
                        {isStarType(q) && <StarPreview q={q} />}
                        {isImageWithComment(q) && <ImageCommentPreview />}

                        {/* 옵션 편집(객관/다중): "한 번에 저장" 방식 */}
                        {(isChoiceSingle(q) || isChoiceMulti(q)) && openOptionEditorIds.includes(q.question_id) && (
                          <div className="option-list" style={{ marginTop: 8 }}>
                            {/* 기존 옵션들 - 행별 입력 but 저장은 맨 아래에서 한 번만 */}
                            {(optionDraft?.items?.length ?? 0) > 0 ? (
                              optionDraft.items.map((opt, i) => {
                                const editKey = `${q.question_id}-${i}`;
                                const active = editingOptionIndex === editKey;
                                return (
                                  <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
                                    <input
                                      className="input-beauty"
                                      value={active ? editingOptionValue : opt}
                                      onChange={(e) => {
                                        // 입력 시작 시 해당 행을 활성화
                                        if (!active) {
                                          setEditingOptionIndex(editKey);
                                          setEditingOptionValue(opt);
                                        } else {
                                          setEditingOptionValue(e.target.value);
                                        }
                                        // draft에는 입력값 실시간 반영
                                        const next = [...(optionDraft.items || [])];
                                        const val = active ? e.target.value : e.target.value;
                                        next[i] = val;
                                        patchDraft(q.question_id, { items: next });
                                      }}
                                      onBlur={() => {
                                        // 포커스 벗어나면 행 활성화 상태만 해제(저장은 전체 저장 버튼에서)
                                        setEditingOptionIndex(null);
                                        setEditingOptionValue('');
                                      }}
                                    />
                                    <button
                                      className="edit-delete"
                                      title="삭제"
                                      onClick={() => {
                                        const next = (optionDraft.items || []).filter((_, idx) => idx !== i);
                                        patchDraft(q.question_id, { items: next });
                                      }}
                                    >
                                      삭제🗑️
                                    </button>
                                  </div>
                                );
                              })
                            ) : (
                              <div className="sub-muted">등록된 옵션이 없습니다.</div>
                            )}

                            {/* 새 옵션 추가 (드래프트에만 추가됨) */}
                            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8 }}>
                              <input
                                className="input-beauty"
                                placeholder="새 옵션"
                                value={newOptionValueForEdit}
                                onChange={(e) => setNewOptionValueForEdit(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    const v = (newOptionValueForEdit || '').trim();
                                    if (!v) return;
                                    if (v === '기타') {
                                      patchDraft(q.question_id, { includeEtc: true });
                                    } else {
                                      const next = [...(optionDraft.items || []), v];
                                      patchDraft(q.question_id, { items: next });
                                    }
                                    setNewOptionValueForEdit('');
                                  }
                                }}
                              />
                              <button
                                className="detail-add-button"
                                onClick={() => {
                                  const v = (newOptionValueForEdit || '').trim();
                                  if (!v) return;
                                  if (v === '기타') {
                                    patchDraft(q.question_id, { includeEtc: true });
                                  } else {
                                    const next = [...(optionDraft.items || []), v];
                                    patchDraft(q.question_id, { items: next });
                                  }
                                  setNewOptionValueForEdit('');
                                }}
                              >
                                추가
                              </button>
                            </div>

                            {/* '기타' 토글 */}
                            <label className="question-explain" style={{ marginTop: 6, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                              <input
                                type="checkbox"
                                checked={!!optionDraft?.includeEtc}
                                onChange={(e) => patchDraft(q.question_id, { includeEtc: e.target.checked })}
                              />
                              '기타' 옵션 포함
                            </label>

                            {/* 하단 한 번에 저장/취소 */}
                            <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                              <button className="detail-add-button" disabled={isSavingOrder} onClick={() => saveOptionsFor(q)}>
                                변경사항 저장
                              </button>
                              <button
                                className="edit-delete"
                                onClick={() => {
                                  // 편집 취소: 드래프트 롤백 + 창 닫기
                                  setOpenOptionEditorIds((prev) => prev.filter((id) => id !== q.question_id));
                                  setEditingOptionIndex(null);
                                  setEditingOptionValue('');
                                  setNewOptionValueForEdit('');
                                }}
                              >
                                취소
                              </button>
                            </div>
                          </div>
                        )}

                        {/* 카드 하단: 액션 */}
                        <div className="edit-delete-icon-group" style={{ marginTop: 10, gap: 6, flexWrap: 'wrap' }}>
                          {(isChoiceSingle(q) || isChoiceMulti(q)) && (
                            <button className="edit-delete small-icon" disabled={isSavingOrder} onClick={() => toggleOptionEditor(q.question_id)} title="옵션 편집">
                              옵션수정🔧
                            </button>
                          )}


                          {(
                            <button className="edit-delete" data-edit-question disabled={isSavingOrder} onClick={() => startEdit(q)}>
                              수정✏️
                            </button>
                          )}


                          {/* 유형 전환 버튼: 객관식 <-> 다중선택 */}
                          {(isChoiceSingle(q) || isChoiceMulti(q)) && (
                            <button className="edit-delete small-icon" disabled={isSavingOrder} onClick={() => toggleChoiceType(q)} title="유형 전환">
                              {isChoiceSingle(q) ? '다중선택변경🗒️' : '객관식변경🗒️'}
                            </button>
                          )}

                          <button
                            disabled={isSavingOrder}
                            onClick={() => {
                              if (window.confirm('정말 삭제하시겠습니까?')) deleteQuestion(q.question_id);
                            }}
                            className="edit-delete"
                          >
                            삭제🗑️
                          </button>

                          <button className="edit-delete small-icon" disabled={isSavingOrder} onClick={() => toggleRequired(q)} title="필수 여부 전환">
                            {q.required ? '필수해제' : '필수설정'}
                          </button>
                        </div>
                        </>}
                      </li>
                    )}
                  </Draggable>
                );
              })}
              {provided.placeholder}
            </ul>
          )}
        </Droppable>
      </DragDropContext>

      {/* 문항 추가 */}
      <div className="question-add-form">
        <h3>📋문항 추가</h3>

        <input
          type="text"
          placeholder="문항 제목"
          value={newQuestion.question_text}
          onChange={(e) => setNewQuestion({ ...newQuestion, question_text: e.target.value })}
          className="input-beauty"
        />
        <input
          type="text"
          placeholder="문항 설명"
          value={newQuestion.question_explain || ''}
          onChange={(e) => setNewQuestion({ ...newQuestion, question_explain: e.target.value })}
          className="input-beauty"
        />

        <select
          value={newQuestion.question_type_id}
          onChange={(e) => setNewQuestion({ ...newQuestion, question_type_id: Number(e.target.value) })}
          className="input-beauty"
        >
          <option value="">문항 유형 선택</option>
          {questionTypes.map((type) => (
            <option key={type.type_id} value={type.type_id}>
              {type.display_name}
            </option>
          ))}
        </select>

        {/* 객관/다중 옵션 - 새 문항 */}
        {[2, 4].includes(Number(newQuestion.question_type_id)) && (
          <div className="option-list">
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input
                className="input-beauty"
                placeholder="옵션 내용 입력"
                value={newOptionValue}
                onChange={(e) => setNewOptionValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    const v = (newOptionValue || '').trim();
                    if (!v) return;
                    if (v === '기타') setNewQuestion((prev) => ({ ...prev, includeEtc: true }));
                    else setNewQuestion((prev) => ({ ...prev, options: [...(prev.options || []), v] }));
                    setNewOptionValue('');
                  }
                }}
              />
              <button
                className="detail-add-button"
                onClick={() => {
                  const v = (newOptionValue || '').trim();
                  if (!v) return;
                  if (v === '기타') setNewQuestion((prev) => ({ ...prev, includeEtc: true }));
                  else setNewQuestion((prev) => ({ ...prev, options: [...(prev.options || []), v] }));
                  setNewOptionValue('');
                }}
              >
                옵션 추가
              </button>
            </div>

            <ul style={{ marginTop: 6 }}>
              {(newQuestion.options || []).map((opt, idx) => (
                <li key={idx} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span>{opt}</span>
                  <button
                    className="edit-delete"
                    onClick={() => setNewQuestion((prev) => ({ ...prev, options: prev.options.filter((_, i) => i !== idx) }))}
                  >
                    삭제🗑️
                  </button>
                </li>
              ))}
            </ul>

            <label className="question-explain" style={{ marginTop: 6 }}>
              <input
                type="checkbox"
                checked={!!newQuestion.includeEtc}
                onChange={(e) => setNewQuestion((prev) => ({ ...prev, includeEtc: e.target.checked }))}
              />{' '}
              '기타' 옵션 추가
            </label>
          </div>
        )}

        {/* 별점 전용 입력 */}
        {Number(newQuestion.question_type_id) === 3 && (
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginTop: 6, flexWrap: 'wrap' }}>
            <label className="question-explain">
              최대 점수
              <input
                type="number"
                min={1}
                max={10}
                className="input-beauty"
                style={{ width: 100, marginLeft: 8 }}
                value={newQuestion.max_score ?? 5}
                onChange={(e) => setNewQuestion((p) => ({ ...p, max_score: Math.max(1, Math.min(10, Number(e.target.value || 5))) }))}
              />
            </label>
            <label className="question-explain">
              간격(step)
              <input
                type="number"
                step="0.5"
                min={0.5}
                max={5}
                className="input-beauty"
                style={{ width: 100, marginLeft: 8 }}
                value={newQuestion.step ?? 1}
                onChange={(e) => setNewQuestion((p) => ({ ...p, step: Math.max(0.5, Math.min(5, Number(e.target.value || 1))) }))}
              />
            </label>
          </div>
        )}

        <div>
          <label className="question-explain">
            <input
              type="checkbox"
              checked={!!newQuestion.required}
              onChange={(e) => setNewQuestion({ ...newQuestion, required: e.target.checked })}
            />{' '}
            필수 응답 항목입니다.
          </label>
        </div>

        {/* 정답(선택) 입력 */}
        <div style={{ marginTop: 6 }}>
          <label className="question-explain">정답(선택):</label>
          <input
            type="text"
            value={newQuestion.correct_answer || ''}
            onChange={(e) => setNewQuestion({ ...newQuestion, correct_answer: e.target.value })}
            className="input-beauty"
            placeholder="비워두면 정답 없음"
          />
        </div>

        <button
          disabled={isSavingOrder}
          onClick={() => {
            const payload = { ...newQuestion };
            if (Number(payload.question_type_id) === 3) {
              payload.max_score = Number(payload.max_score ?? 5);
              payload.step = Number(payload.step ?? 1);
            } else {
              delete payload.max_score;
              delete payload.step;
            }
            addQuestion(payload);
          }}
          className="add-button"
        >
          문항 추가
        </button>
      </div>
    </div>
  );
};

export default AdminSurveyManagePageRight;
